import { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import api, { createRepairRecord, updateRepairRecord } from '../../../lib/api';
import { PhotoCapture } from '../../../components/PhotoCapture';
import { AssetDetailsEditBlock } from '../../../components/AssetDetailsEditBlock';
import Button from '../../../components/ui/Button';
import { repairPhotoUrls } from '../utils/repairPhotos';

/**
 * Repair completion modal.
 *
 * A repair is recorded in two stages so a technician can photograph the defect, close the
 * app, do the physical work, and come back: "Save before photo" persists the record on its
 * own, and "Mark repaired" requires the after photo and closes the defect.
 *
 * Both actions persist any edited asset details first, so there is one button press per
 * intent rather than a separate "save details" step that is easy to skip (E2).
 *
 * Props:
 *   defect       — open defect row from GET /api/repairs/open
 *   onClose      — called when the modal is dismissed without submitting
 *   onRepaired   — (record, defect) after the repair is completed
 *   onBeforePhotoSaved — (record, defect) after a before-photo-only save; the defect stays open
 *   existingRecord — existing repair_record, either completed (edit) or in progress (resume)
 *   workOrderNumber — optional work order number for persistence
 */
export function RepairCompleteModal({
  defect,
  onClose,
  onRepaired,
  onBeforePhotoSaved,
  existingRecord = null,
  workOrderNumber = '',
}) {
  const [beforePhotoUrl, setBeforePhotoUrl] = useState('');
  const [afterPhotoUrl, setAfterPhotoUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [missingPhoto, setMissingPhoto] = useState(null);
  const [assetPatch, setAssetPatch] = useState(null);
  const [repairType, setRepairType] = useState('repaired');
  const assetDetailsRef = useRef(null);

  const assetId = defect?.asset_id != null ? Number(defect.asset_id) : null;
  const { data: assetRow, isLoading: assetLoading, error: assetError } = useQuery(
    ['asset', assetId],
    () => api.get(`/assets/${assetId}`).then((res) => res.data),
    { enabled: Number.isInteger(assetId) && assetId > 0, staleTime: 0 }
  );

  const assetForEdit = assetRow
    ? assetPatch && assetPatch.id === assetRow.id
      ? { ...assetRow, ...assetPatch }
      : assetRow
    : null;

  // A record that already carries an after photo is a completed repair being edited;
  // one with only a before photo is an in-progress repair being resumed.
  const existingPhotos = repairPhotoUrls(existingRecord);
  const isResumingInProgress = Boolean(existingRecord?.id) && !existingPhotos.afterPhotoUrl;

  useEffect(() => {
    const { beforePhotoUrl: existingBefore, afterPhotoUrl: existingAfter } = repairPhotoUrls(existingRecord);
    setAssetPatch(null);
    setBeforePhotoUrl(existingBefore);
    setAfterPhotoUrl(existingAfter);
    setNotes(existingRecord?.notes || '');
    setMissingPhoto(null);
    setRepairType(existingRecord?.repair_type || existingRecord?.repairType || 'repaired');
  }, [defect?.asset_id, existingRecord]);

  /**
   * A stale list can offer "Mark Repaired" for a defect that already has a saved before
   * photo. The API rejects the create but names the record, so finish that one rather
   * than dead-ending the tech on a conflict they cannot clear from the UI.
   */
  const createWithResume = async (payload) => {
    try {
      return await createRepairRecord(payload);
    } catch (err) {
      const inProgressId = err.response?.status === 409 ? err.response?.data?.repairId : null;
      if (!inProgressId) throw err;
      return updateRepairRecord(inProgressId, payload);
    }
  };

  /**
   * @param {{ complete: boolean }} intent `complete` requires the after photo and closes the
   *   defect; otherwise only the before photo is persisted and the defect stays open.
   */
  const persistRepair = async ({ complete }) => {
    if (!beforePhotoUrl) {
      setMissingPhoto('before');
      return;
    }
    if (complete && !afterPhotoUrl) {
      setMissingPhoto('after');
      return;
    }
    setSubmitting(true);
    // E2: whichever action the tech pressed also saves any edited asset details, so the
    // details cannot be lost by skipping a separate save.
    try {
      await assetDetailsRef.current?.saveIfNeeded();
    } catch {
      setSubmitting(false);
      return;
    }
    try {
      const payload = {
        assetId: defect.asset_id,
        sourceTestResultId: defect.test_result_id,
        beforePhotoUrl,
        ...(complete ? { afterPhotoUrl } : {}),
        repairType,
        workOrderNumber: String(workOrderNumber || '').trim() || undefined,
        notes: notes.trim() || undefined,
      };
      const record = existingRecord?.id
        ? await updateRepairRecord(existingRecord.id, payload)
        : await createWithResume(payload);

      if (!complete) {
        toast.success('Before photo saved — finish this repair when the work is done');
        onBeforePhotoSaved?.(record, defect);
        return;
      }
      if (existingRecord?.id && !isResumingInProgress) {
        toast.success('Repair record updated');
      } else {
        toast.success(repairType === 'replaced' ? 'Replacement recorded' : 'Repair recorded');
      }
      onRepaired(record, defect);
    } catch (err) {
      const msg =
        err.response?.data?.errors?.[0]?.msg ||
        err.response?.data?.error ||
        'Failed to record repair';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    persistRepair({ complete: true });
  };

  const canSaveBeforeOnly = Boolean(beforePhotoUrl) && !afterPhotoUrl;
  const submitLabel =
    existingRecord?.id && !isResumingInProgress
      ? 'Save changes'
      : (repairType === 'replaced' ? 'Mark replaced' : 'Mark repaired');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal repair-complete-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="repair-modal-title"
      >
        <h2 id="repair-modal-title">{existingRecord?.id ? 'Edit repair record' : 'Record repair'}</h2>

        {defect.defects_found ? (
          <p className="repair-modal-defect-note">
            <strong>Defect:</strong> {defect.defects_found}
          </p>
        ) : null}

        {isResumingInProgress ? (
          <p className="repair-modal-resume-note text-muted">
            Before photo already saved. Add the after photo to finish this repair.
          </p>
        ) : null}

        <div className="form-group repair-resolution-group" style={{ marginTop: '0.75rem' }}>
          <span className="form-label-block" style={{ marginBottom: '0.35rem' }}>
            Resolution
          </span>
          <div className="repair-resolution-options">
            <label className="repair-resolution-option">
              <input
                type="radio"
                name="repair-resolution"
                checked={repairType === 'repaired'}
                onChange={() => setRepairType('repaired')}
                disabled={submitting}
              />
              <span>Repaired (same fitting)</span>
            </label>
            <label className="repair-resolution-option">
              <input
                type="radio"
                name="repair-resolution"
                checked={repairType === 'replaced'}
                onChange={() => setRepairType('replaced')}
                disabled={submitting}
              />
              <span>Replaced (new fitting)</span>
            </label>
          </div>
          {repairType === 'replaced' ? (
            <p className="text-muted text-xs" style={{ marginTop: '0.35rem', marginBottom: 0 }}>
              Update make, model, and equipment details below so the asset record matches the new
              fitting.
            </p>
          ) : null}
        </div>

        {assetLoading && assetId ? (
          <p className="text-muted" style={{ marginBottom: '1rem' }}>
            Loading asset…
          </p>
        ) : null}
        {assetError && assetId ? (
          <p className="form-error" style={{ marginBottom: '1rem' }}>
            Failed to load asset details for editing.
          </p>
        ) : null}
        {assetForEdit ? (
          <AssetDetailsEditBlock
            ref={assetDetailsRef}
            asset={assetForEdit}
            onUpdated={(updated) => setAssetPatch(updated)}
            startInEditMode={repairType === 'replaced'}
          />
        ) : null}

        <form onSubmit={handleSubmit}>
          <PhotoCapture
            currentUrl={beforePhotoUrl}
            onUploadComplete={(url) => setBeforePhotoUrl(url || '')}
            label="Before photo (required)"
          />
          <PhotoCapture
            currentUrl={afterPhotoUrl}
            onUploadComplete={(url) => setAfterPhotoUrl(url || '')}
            label="After photo (required to finish)"
          />

          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label htmlFor="repair-notes">Notes (optional)</label>
            <textarea
              id="repair-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Describe what was repaired…"
              disabled={submitting}
            />
          </div>

          <div className="form-actions">
            <Button type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            {canSaveBeforeOnly && (
              <Button
                type="button"
                onClick={() => persistRepair({ complete: false })}
                disabled={submitting}
              >
                Save before photo
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : submitLabel}
            </Button>
          </div>
        </form>

        {missingPhoto && (
          <div className="repair-photo-required-dialog" role="alertdialog" aria-modal="true" aria-labelledby="repair-photo-required-title">
            <h3 id="repair-photo-required-title">
              {missingPhoto === 'before' ? 'Before photo required' : 'After photo required'}
            </h3>
            <p>
              {missingPhoto === 'before'
                ? 'Take the before photo first. You can save it on its own and come back once the repair is done.'
                : 'Upload the after photo to mark this item as repaired, or save the before photo on its own and come back later.'}
            </p>
            <div className="repair-photo-required-actions">
              <Button
                type="button"
                variant="primary"
                onClick={() => setMissingPhoto(null)}
              >
                OK
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
